import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type Work, type Episode, type TodayPlan } from '../api/client'
import { ConfirmDialog, UndoToast } from '../components/ConfirmDialog'
import TodayProductionPlanPanel from '../components/TodayProductionPlanPanel'
import { todayJst } from '../utils/jst'
import { useAuth } from '../components/AuthGuard'

const STAGE_LABELS: Record<string, string> = {
  name: 'ネーム', name_revision: 'ネーム修正', sketch: '下書き',
  inking: 'ペン入れ', background: '背景', finishing: '仕上げ', submitted: '納品',
}
const STAGES = Object.keys(STAGE_LABELS)

const WORK_TYPE_LABELS: Record<string, string> = {
  story:    'ストーリー',
  tankobon: '単行本',
  color:    'カラー',
}

// 作品（works）の頻度＝作品追加フォームの「種別」に使う既存分類
const FREQUENCY_LABELS: Record<string, string> = {
  weekly:    '週刊',
  monthly:   '月刊',
  bimonthly: '隔月刊',
  irregular: '不定期',
}
const WORK_TYPE_BADGE: Record<string, string> = {
  story:    'badge-blue',
  tankobon: 'badge-amber',
  color:    'badge-green',
}

/** 話数ラベル: ストーリーは #番号、単行本・カラーは work_label */
function epLabel(ep: Pick<Episode, 'work_type' | 'episode_number' | 'work_label'>): string {
  if (ep.work_type !== 'story') return ep.work_label ?? '(ラベルなし)'
  return `#${ep.episode_number}`
}

// 締切近い順ソート用：各話数の「最も近いアクティブな締切日」を返す
// 完了済みの工程に対応する締切は除外する
function nearestActiveDeadline(ep: any): Date | null {
  const nameDone   = isNameCheckDone(ep)
  const submitDone = isSubmitDone(ep)
  const candidates: Date[] = []

  if (!nameDone && ep.deadline_name_check) {
    const d = new Date(ep.deadline_name_check)
    if (!isNaN(d.getTime())) candidates.push(d)
  }
  if (!submitDone && ep.deadline_submit) {
    const d = new Date(ep.deadline_submit)
    if (!isNaN(d.getTime())) candidates.push(d)
  }
  if (!submitDone && ep.deadline_entry) {
    const d = new Date(ep.deadline_entry)
    if (!isNaN(d.getTime())) candidates.push(d)
  }
  if (candidates.length === 0) return null
  return candidates.reduce((min, d) => d < min ? d : min)
}

const DONE_STATUSES = new Set(['completed', 'submitted', 'hiatus'])

// ─── 工程完了チェックユーティリティ ───────────────────────────────
//
// episodes.ts の GET / は GROUP_CONCAT(ps.stage || ':' || ps.status) AS stage_summary
// を返しており、例: "name:done,name_revision:done,sketch:in_progress,..."
//
// 各締切カラムに対応する「完了すべき工程」を個別に確認する。
//   deadline_name_check → name 工程が done
//   deadline_submit     → submitted 工程が done
//   deadline_entry      → submitted 工程が done（入稿 = 最終提出）
// 話数全体が DONE_STATUSES の場合はすべて非表示。

function parseStageMap(ep: any): Record<string, string> {
  const map: Record<string, string> = {}
  const summary = String((ep as any).stage_summary ?? '')
  if (!summary) return map
  for (const pair of summary.split(',')) {
    const idx = pair.indexOf(':')
    if (idx < 0) continue
    const s  = pair.slice(0, idx).trim()
    const st = pair.slice(idx + 1).trim()
    if (s && st) map[s] = st
  }
  return map
}

/** 話数全体が完了済みか（行の opacity や archive 判定に使う） */
function isEpisodeEffectivelyDone(ep: any): boolean {
  if (DONE_STATUSES.has(ep.status)) return true
  const map = parseStageMap(ep)
  return (
    Object.keys(map).length > 0 &&
    Object.values(map).every((s) => s === 'done')
  )
}

/** ネーム締切（deadline_name_check）を非表示にするか
 *  name 工程が done になっていれば、ネーム確認は完了済みとみなす */
function isNameCheckDone(ep: any): boolean {
  if (DONE_STATUSES.has(ep.status)) return true
  const map = parseStageMap(ep)
  return map['name'] === 'done'
}

/** 提出締切・入稿（deadline_submit / deadline_entry）を非表示にするか
 *  submitted 工程が done になっていれば完了済みとみなす */
function isSubmitDone(ep: any): boolean {
  if (DONE_STATUSES.has(ep.status)) return true
  const map = parseStageMap(ep)
  return map['submitted'] === 'done'
}

function daysUntil(s: string | null) {
  if (!s) return null
  return Math.ceil((new Date(s + 'T00:00:00').getTime() - new Date(todayJst() + 'T00:00:00').getTime()) / 86400000)
}

// ★ status を受け取り、完了済みなら期限超過表示をスキップ
function DeadlineChip({ days, status }: { days: number | null; status?: string }) {
  if (status && DONE_STATUSES.has(status)) {
    return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
  }
  if (days === null) return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
  if (days < 0)  return <span className="deadline-chip deadline-critical">{Math.abs(days)}日超過</span>
  if (days === 0) return <span className="deadline-chip deadline-critical">今日</span>
  if (days <= 3)  return <span className="deadline-chip deadline-critical">残{days}日</span>
  if (days <= 7)  return <span className="deadline-chip deadline-warning">残{days}日</span>
  return <span className="deadline-chip deadline-ok">残{days}日</span>
}

function StagePips({ summary, progressSummary }: { summary?: string; progressSummary?: string }) {
  if (!summary) return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
  const statusMap: Record<string, string> = {}
  for (const pair of summary.split(',')) {
    const [stage, st] = pair.split(':')
    if (stage && st) statusMap[stage] = st
  }
  // "stage:progress:target,..." → tooltip 用テキスト
  const progressMap: Record<string, string> = {}
  if (progressSummary) {
    for (const item of progressSummary.split(',')) {
      const parts = item.split(':')
      if (parts.length >= 2 && parts[0]) {
        const prog = parts[1] || '0'
        const tgt  = parts[2] && parts[2] !== '' ? parts[2] : null
        progressMap[parts[0]] = tgt ? `${prog}/${tgt}P` : `${prog}P`
      }
    }
  }
  return (
    <div className="stage-bar">
      {STAGES.map((s) => {
        const progText = progressMap[s] ? ` (${progressMap[s]})` : ''
        return (
          <div key={s} className={`stage-pip ${statusMap[s] ?? 'not_started'}`}
            title={`${STAGE_LABELS[s]}: ${statusMap[s] ?? '-'}${progText}`} />
        )
      })}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    planned: 'badge-gray', in_progress: 'badge-amber',
    submitted: 'badge-blue', completed: 'badge-green', hiatus: 'badge-gray',
  }
  const labels: Record<string, string> = {
    planned: '予定', in_progress: '進行中', submitted: '提出済', completed: '完了', hiatus: '休載',
  }
  return <span className={`badge ${map[status] ?? 'badge-gray'}`}>{labels[status] ?? status}</span>
}

// =====================================================
// 話数追加モーダル
// =====================================================
function AddEpisodeModal({ works, defaultWorkId, onClose, onSaved }: {
  works: Work[]
  defaultWorkId?: string
  onClose: () => void
  onSaved: (id: string, workTitle: string, label: string) => void
}) {
  const [form, setForm] = useState({
    work_id:            defaultWorkId ?? works[0]?.id ?? '',
    work_type:          'story' as 'story' | 'tankobon' | 'color',
    episode_number:     '',
    work_label:         '',
    page_count:         '',
    deadline_name_check: '', deadline_submit: '', deadline_entry: '',
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => { const n = { ...e }; delete n[k]; return n })
    setSubmitError(null)
  }

  const isStory = form.work_type === 'story'

  const handleSubmit = async () => {
    const e: Record<string, string> = {}
    if (!form.work_id) e.work_id = '作品を選択してください'
    if (isStory) {
      if (!form.episode_number)              e.episode_number = '話数番号を入力してください'
      if (form.episode_number && isNaN(Number(form.episode_number))) e.episode_number = '数値を入力してください'
    } else {
      if (!form.work_label.trim()) e.work_label = 'ラベルを入力してください（例: 単行本1巻 カバー修正）'
    }
    if (Object.keys(e).length > 0) { setErrors(e); return }
    setSaving(true)
    try {
      const result: any = await api.episodes.create({
        work_id:             form.work_id,
        work_type:           form.work_type,
        episode_number:      isStory ? Number(form.episode_number) : undefined,
        work_label:          isStory ? undefined : form.work_label.trim(),
        page_count:          form.page_count ? Number(form.page_count) : undefined,
        deadline_name_check: form.deadline_name_check || undefined,
        deadline_submit:     form.deadline_submit     || undefined,
        deadline_entry:      form.deadline_entry      || undefined,
      })
      const work  = works.find((w) => w.id === form.work_id)
      const label = isStory ? `#${form.episode_number}` : form.work_label
      onSaved(result?.id ?? '', work?.title ?? '', label)
    } catch (ex: any) {
      setSubmitError(ex.message ?? 'エラーが発生しました')
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">話数・作業を追加</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>作品 <span style={{ color: 'var(--red)' }}>*</span></label>
            <select value={form.work_id} onChange={(e) => set('work_id', e.target.value)}
              style={{ borderColor: errors.work_id ? 'var(--red)' : undefined }}>
              {works.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
            </select>
            {errors.work_id && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.work_id}</div>}
          </div>

          {/* 作業種別 */}
          <div className="form-group">
            <label>作業種別 <span style={{ color: 'var(--red)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              {(Object.entries(WORK_TYPE_LABELS) as ['story' | 'tankobon' | 'color', string][]).map(([v, l]) => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" checked={form.work_type === v} onChange={() => set('work_type', v)} />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {/* ストーリー: 話数番号 / 単行本・カラー: ラベル */}
          <div className="grid-2">
            {isStory ? (
              <div className="form-group">
                <label>話数番号 <span style={{ color: 'var(--red)' }}>*</span></label>
                <input type="number" placeholder="101" value={form.episode_number}
                  onChange={(e) => set('episode_number', e.target.value)}
                  style={{ borderColor: errors.episode_number ? 'var(--red)' : undefined }} />
                {errors.episode_number && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.episode_number}</div>}
              </div>
            ) : (
              <div className="form-group">
                <label>ラベル <span style={{ color: 'var(--red)' }}>*</span></label>
                <input type="text" placeholder="例: 単行本1巻 カバー修正" value={form.work_label}
                  onChange={(e) => set('work_label', e.target.value)}
                  style={{ borderColor: errors.work_label ? 'var(--red)' : undefined }} />
                {errors.work_label && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.work_label}</div>}
              </div>
            )}
            <div className="form-group">
              <label>ページ数</label>
              <input type="number" placeholder={isStory ? '24' : '0'} value={form.page_count}
                onChange={(e) => set('page_count', e.target.value)} />
            </div>
          </div>

          <div className="grid-3">
            <div className="form-group">
              <label>ネーム確認締切</label>
              <input type="date" value={form.deadline_name_check} onChange={(e) => set('deadline_name_check', e.target.value)} />
            </div>
            <div className="form-group">
              <label>提出締切</label>
              <input type="date" value={form.deadline_submit} onChange={(e) => set('deadline_submit', e.target.value)} />
            </div>
            <div className="form-group">
              <label>入稿日</label>
              <input type="date" value={form.deadline_entry} onChange={(e) => set('deadline_entry', e.target.value)} />
            </div>
          </div>
        </div>
        {submitError && (
          <div style={{
            padding: '8px 16px',
            background: 'var(--red-light, #fef2f2)',
            borderTop: '1px solid var(--red, #ef4444)',
            color: 'var(--red, #ef4444)',
            fontSize: 13,
          }}>
            {submitError}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '保存中...' : '追加'}
          </button>
        </div>
      </div>
    </div>
  )
}

// =====================================================
// 今日の制作指示パネル
// =====================================================
const RISK_STYLE: Record<string, { bg: string; border: string; color: string }> = {
  critical: { bg: 'var(--red-light)',   border: 'var(--red)',   color: 'var(--red)'   },
  danger:   { bg: 'var(--red-light)',   border: 'var(--red)',   color: 'var(--red)'   },
  warning:  { bg: 'var(--amber-light)', border: 'var(--amber)', color: 'var(--amber)' },
  safe:     { bg: 'var(--green-light)', border: 'var(--green)', color: 'var(--green)' },
}

function TodayPlanPanel({ reloadKey }: { reloadKey: number }) {
  const [plan, setPlan]       = useState<TodayPlan | null>(null)
  const [planLoading, setPlanLoading] = useState(true)
  const [planError, setPlanError]     = useState<string | null>(null)

  useEffect(() => {
    setPlanLoading(true)
    setPlanError(null)
    api.productionPlanning
      .getTodayPlan()
      .then(setPlan)
      .catch((err: Error) => setPlanError(err.message))
      .finally(() => setPlanLoading(false))
  }, [reloadKey])

  const DISPLAY_LIMIT = 6

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">今日の制作指示</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <Link to="/works/progress-settings" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>制作進行設定</button>
          </Link>
          <Link to="/works/production-plan" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>制作計画</button>
          </Link>
        </div>
      </div>
      <div className="card-body">
        {planLoading && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>計算中...</div>
        )}
        {!planLoading && planError && (
          <div style={{ color: 'var(--red)', fontSize: 13 }}>
            計算エラー: {planError}
          </div>
        )}
        {!planLoading && !planError && plan && plan.recommendations.length === 0 && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
            今日の制作指示はありません（締切未設定の話数のみか、すべて安全圏です）
          </div>
        )}
        {!planLoading && !planError && plan && plan.recommendations.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plan.recommendations.slice(0, DISPLAY_LIMIT).map((rec) => {
              const s = RISK_STYLE[rec.risk] ?? RISK_STYLE.safe
              const amountText =
                rec.unit === 'pages' ? `${rec.suggested_amount}P` :
                rec.unit === 'days'  ? `${rec.suggested_amount}日` : ''
              return (
                <div
                  key={rec.episode_id}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 14px',
                    background: s.bg,
                    borderLeft: `3px solid ${s.border}`,
                    borderRadius: 6,
                  }}
                >
                  <div style={{ minWidth: 70, paddingTop: 1 }}>
                    <span
                      className="badge"
                      style={{
                        background: s.bg,
                        color: s.color,
                        border: `1px solid ${s.border}`,
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {rec.risk_label}
                    </span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                      {rec.work_title} {rec.episode_label}
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 3 }}>
                      {rec.stage_label}{amountText ? ` ${amountText}` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {rec.reason}
                      {'　'}
                      <span style={{ fontFamily: 'monospace' }}>
                        (余裕 {rec.slack_days}日 / 締切 {rec.deadline})
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
            {plan.recommendations.length > DISPLAY_LIMIT && (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'center', paddingTop: 4 }}>
                他 {plan.recommendations.length - DISPLAY_LIMIT} 件あります
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// =====================================================
// 工程更新モーダル (Phase 2.5: 進捗ページ数入力対応)
// =====================================================

/** ページ数入力が必要な工程（日数ベース・納品は除外） */
const PAGES_STAGES = new Set(['sketch', 'inking', 'background', 'finishing'])

type StageProgressState = {
  progress_pages: string
  target_pages: string
  progress_note: string
}

function StageModal({ episode, onClose, onSaved }: {
  episode: Episode & { stages?: any[] }
  onClose: () => void
  onSaved: () => void
}) {
  const [stages, setStages]               = useState<any[]>([])
  const [loading, setLoading]             = useState(true)
  const [saving, setSaving]               = useState<string | null>(null)
  const [episodeStatus, setEpisodeStatus] = useState<string>(episode.status)
  const [progressState, setProgressState] = useState<Record<string, StageProgressState>>({})
  const [syncNote, setSyncNote]           = useState<string | null>(null)

  /** DB から工程一覧を取得してローカル状態に反映 */
  const loadStages = async () => {
    const data = await api.episodes.get(episode.id)
    const list = data.stages ?? []
    setStages(list)
    setEpisodeStatus((data as any).status ?? episode.status)
    const pm: Record<string, StageProgressState> = {}
    for (const s of list) {
      pm[s.stage] = {
        progress_pages: s.progress_pages != null && Number(s.progress_pages) !== 0
          ? String(s.progress_pages) : '',
        target_pages:   s.target_pages != null ? String(s.target_pages) : '',
        progress_note:  s.progress_note ?? '',
      }
    }
    setProgressState(pm)
    return data
  }

  useEffect(() => {
    loadStages().finally(() => setLoading(false))
  }, [episode.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const getProgress = (stage: string): StageProgressState =>
    progressState[stage] ?? { progress_pages: '', target_pages: '', progress_note: '' }

  const setProgressField = (
    stage: string,
    field: keyof StageProgressState,
    value: string,
  ) => {
    setProgressState((prev) => ({
      ...prev,
      [stage]: { ...getProgress(stage), [field]: value },
    }))
  }

  /**
   * ステータス変更 OR 進捗のみ保存。
   * どちらも同一エンドポイント (PATCH /:id/stages/:stage) を使う。
   */
  const doSave = async (
    stage: string,
    newStatus: 'not_started' | 'in_progress' | 'done',
  ) => {
    setSaving(stage)
    setSyncNote(null)
    try {
      const prevStatus = stages.find((s) => s.stage === stage)?.status
      const p = getProgress(stage)
      const progress = PAGES_STAGES.has(stage) ? {
        progress_pages: p.progress_pages !== '' ? Number(p.progress_pages) : undefined,
        target_pages:   p.target_pages   !== '' ? Number(p.target_pages)   : null,
        progress_note:  p.progress_note  || null,
      } : undefined
      const result: any = await api.episodes.updateStage(
        episode.id, stage, newStatus, progress,
      )
      if (result?.episode_status) setEpisodeStatus(result.episode_status)

      // 制作計画側の同期状況を控えめに通知（完了/完了取り消しのときだけ）
      // newStatus === 'done' が false の時点で newStatus !== 'done' は自明なため、
      // prevStatus === 'done' の判定だけで「done から戻した」ケースを表せる
      const isMeaningfulSync = newStatus === 'done' || prevStatus === 'done'
      if (isMeaningfulSync) {
        const updated = result?.plan_sync?.updated ?? 0
        setSyncNote(
          updated === 0 ? '該当する制作予定はありませんでした'
          : updated === 1 ? '制作予定にも反映しました'
          : `制作予定を${updated}件更新しました`
        )
      }

      await loadStages()
      onSaved()
    } finally {
      setSaving(null)
    }
  }

  /** 進捗のみ保存（ステータスは現状維持） */
  const saveProgressOnly = async (stage: string) => {
    const stageData = stages.find((s) => s.stage === stage)
    if (!stageData) return
    await doSave(stage, stageData.status)
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="modal-title">{episode.work_title} {epLabel(episode)} 工程</div>
            <StatusBadge status={episodeStatus} />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        {syncNote && (
          <div style={{
            padding: '4px 20px', fontSize: 11, color: 'var(--ink-3)',
            background: 'var(--paper-2)', borderBottom: '1px solid var(--paper-3)',
          }}>
            {syncNote}
          </div>
        )}
        <div className="modal-body">
          {loading ? <div className="loading">...</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {stages.map((s) => {
                const isPages   = PAGES_STAGES.has(s.stage)
                const isSaving  = saving === s.stage
                const p         = getProgress(s.stage)
                const pageCount = episode.page_count ?? 24
                const totalPages = p.target_pages !== '' ? Number(p.target_pages) : pageCount
                const donePages  = p.progress_pages !== '' ? Number(p.progress_pages) : (s.progress_pages ?? 0)
                const progressPct = totalPages > 0 ? Math.round(donePages / totalPages * 100) : 0
                const isCompletionCandidate =
                  isPages && donePages >= totalPages && s.status !== 'done'

                return (
                  <div key={s.stage} style={{
                    background: 'var(--paper-2)', borderRadius: 8,
                    padding: '12px 14px',
                  }}>
                    {/* ── ヘッダー行: 工程名 + ステータスボタン ── */}
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 8,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          {STAGE_LABELS[s.stage] ?? s.stage}
                        </span>
                        {s.completed_at && (
                          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                            完了: {s.completed_at.slice(0, 10)}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        {(['not_started', 'in_progress', 'done'] as const).map((st) => (
                          <button
                            key={st}
                            className={`btn btn-sm ${s.status === st ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => doSave(s.stage, st)}
                            disabled={!!saving}
                          >
                            {isSaving ? '...' :
                              st === 'not_started' ? '未着' :
                              st === 'in_progress' ? '作業中' : '完了'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── 進捗入力 (ページ数ベース工程のみ) ── */}
                    {isPages && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--paper-3)' }}>
                        {/* 進捗ページ数 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: 'var(--ink-2)', minWidth: 30 }}>進捗</span>
                          <input
                            type="number" min="0" step="1"
                            value={p.progress_pages}
                            onChange={(e) => setProgressField(s.stage, 'progress_pages', e.target.value)}
                            style={{ width: 68, textAlign: 'right', fontSize: 13 }}
                            placeholder="0"
                            disabled={!!saving}
                          />
                          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>/</span>
                          <input
                            type="number" min="1" step="1"
                            value={p.target_pages}
                            onChange={(e) => setProgressField(s.stage, 'target_pages', e.target.value)}
                            style={{ width: 68, textAlign: 'right', fontSize: 13 }}
                            placeholder={String(pageCount)}
                            disabled={!!saving}
                          />
                          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>P</span>
                          {(p.progress_pages !== '' || donePages > 0) && (
                            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', minWidth: 36 }}>
                              {progressPct}%
                            </span>
                          )}
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => saveProgressOnly(s.stage)}
                            disabled={!!saving}
                            style={{ fontSize: 11 }}
                          >
                            {isSaving ? '...' : '進捗保存'}
                          </button>
                        </div>
                        {/* メモ */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--ink-2)', minWidth: 30 }}>メモ</span>
                          <input
                            type="text"
                            value={p.progress_note}
                            onChange={(e) => setProgressField(s.stage, 'progress_note', e.target.value)}
                            style={{ flex: 1, fontSize: 12 }}
                            placeholder="例: 3〜12P完了、難しいページが残り"
                            disabled={!!saving}
                          />
                        </div>
                        {/* 完了候補メッセージ */}
                        {isCompletionCandidate && (
                          <div style={{
                            marginTop: 8, fontSize: 11, color: 'var(--amber)',
                            padding: '5px 10px',
                            background: 'var(--amber-light)', borderRadius: 4,
                          }}>
                            ページ数上は完了候補です。必要ならステータスを「完了」にしてください。
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 12, padding: '8px 0' }}>
            ※ 全工程が「完了」になると話数ステータスが自動で更新されます
          </div>
        </div>
      </div>
    </div>
  )
}

// =====================================================
// 作品追加・編集モーダル
// =====================================================
function WorkFormModal({ work, onClose, onSaved }: {
  /** null/undefined = 新規作成、値があれば編集 */
  work?: Work | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!work
  const [form, setForm] = useState({
    title:              work?.title ?? '',
    frequency:          work?.frequency ?? 'weekly',
    rate_per_page:      work ? String(work.rate_per_page) : '11000',
    pages_per_ep:       work ? String(work.pages_per_ep) : '24',
    has_original_story: work?.has_original_story === 1,
    publisher:          work?.publisher ?? '',
    editor_name:        work?.editor_name ?? '',
    display_order:      work ? String(work.display_order) : '0',
    notes:              work?.notes ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const set = (k: keyof typeof form, v: string | boolean) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => { const n = { ...e }; delete n[k]; return n })
    setSubmitError(null)
  }

  const handleSubmit = async () => {
    const e: Record<string, string> = {}
    if (!form.title.trim())                              e.title = '作品名を入力してください'
    if (!form.rate_per_page || Number(form.rate_per_page) <= 0) e.rate_per_page = '1以上を入力してください'
    if (!form.pages_per_ep  || Number(form.pages_per_ep)  <= 0) e.pages_per_ep  = '1以上を入力してください'
    if (Object.keys(e).length > 0) { setErrors(e); return }

    setSaving(true)
    try {
      const payload = {
        title:              form.title.trim(),
        frequency:          form.frequency,
        rate_per_page:      Number(form.rate_per_page),
        pages_per_ep:       Number(form.pages_per_ep),
        has_original_story: form.has_original_story ? 1 : 0,
        publisher:          form.publisher.trim()   || undefined,
        editor_name:        form.editor_name.trim() || undefined,
        display_order:      Number(form.display_order) || 0,
        notes:              form.notes.trim() || undefined,
      } as any
      if (isEdit) await api.works.update(work!.id, payload)
      else        await api.works.create(payload)
      onSaved()
    } catch (ex: any) {
      setSubmitError(ex.message ?? 'エラーが発生しました')
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? '作品を編集' : '作品を追加'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>作品名 <span style={{ color: 'var(--red)' }}>*</span></label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)}
              style={{ borderColor: errors.title ? 'var(--red)' : undefined }} />
            {errors.title && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.title}</div>}
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>種別（掲載頻度）</label>
              <select value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
                {Object.entries(FREQUENCY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>表示順</label>
              <input type="number" value={form.display_order}
                onChange={(e) => set('display_order', e.target.value)} />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>1ページ単価（円） <span style={{ color: 'var(--red)' }}>*</span></label>
              <input type="number" min="1" value={form.rate_per_page}
                onChange={(e) => set('rate_per_page', e.target.value)}
                style={{ borderColor: errors.rate_per_page ? 'var(--red)' : undefined }} />
              {errors.rate_per_page && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.rate_per_page}</div>}
            </div>
            <div className="form-group">
              <label>1話ページ数 <span style={{ color: 'var(--red)' }}>*</span></label>
              <input type="number" min="1" value={form.pages_per_ep}
                onChange={(e) => set('pages_per_ep', e.target.value)}
                style={{ borderColor: errors.pages_per_ep ? 'var(--red)' : undefined }} />
              {errors.pages_per_ep && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.pages_per_ep}</div>}
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>出版社</label>
              <input type="text" value={form.publisher} onChange={(e) => set('publisher', e.target.value)} />
            </div>
            <div className="form-group">
              <label>担当編集者</label>
              <input type="text" value={form.editor_name} onChange={(e) => set('editor_name', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.has_original_story}
                onChange={(e) => set('has_original_story', e.target.checked)} />
              原作あり
            </label>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>メモ</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)}
              style={{ minHeight: 60, fontSize: 13 }} placeholder="任意のメモ" />
          </div>
        </div>
        {submitError && (
          <div style={{
            padding: '8px 16px',
            background: 'var(--red-light, #fef2f2)',
            borderTop: '1px solid var(--red, #ef4444)',
            color: 'var(--red, #ef4444)',
            fontSize: 13,
          }}>
            {submitError}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '保存中...' : (isEdit ? '保存' : '追加')}
          </button>
        </div>
      </div>
    </div>
  )
}

// =====================================================
// 作品管理モーダル（一覧・編集・アーカイブ・復元・削除）
// =====================================================
function WorksManagerModal({ onClose, onChanged }: {
  onClose:   () => void
  /** 作品タブ・話数追加候補を再読み込みさせるためのコールバック */
  onChanged: () => void
}) {
  const [works, setWorks]             = useState<Work[]>([])
  const [loading, setLoading]         = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const [editTarget, setEditTarget]   = useState<Work | 'new' | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const ws = await api.works.list({ include_archived: true })
      setWorks(ws)
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const visibleWorks = showArchived ? works : works.filter((w) => !w.archived_at)

  const doArchive = async (w: Work) => {
    setActionError(null)
    try { await api.works.archive(w.id); await load(); onChanged() }
    catch (ex: any) { setActionError(ex.message ?? 'エラーが発生しました') }
  }
  const doRestore = async (w: Work) => {
    setActionError(null)
    try { await api.works.restore(w.id); await load(); onChanged() }
    catch (ex: any) { setActionError(ex.message ?? 'エラーが発生しました') }
  }
  const doDelete = async (id: string) => {
    setActionError(null)
    try { await api.works.delete(id); await load(); onChanged() }
    catch (ex: any) { setActionError(ex.message ?? 'エラーが発生しました') }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760, maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'var(--paper)', zIndex: 1 }}>
          <div className="modal-title">作品を管理</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              アーカイブ済みも表示
            </label>
            <button className="btn btn-primary btn-sm" onClick={() => setEditTarget('new')}>
              ＋ 作品追加
            </button>
          </div>

          {actionError && (
            <div style={{
              marginBottom: 12, borderRadius: 6, padding: '8px 12px', fontSize: 12,
              background: 'var(--red-light, #fef2f2)', color: 'var(--red, #ef4444)',
              border: '1px solid var(--red, #ef4444)',
            }}>
              ⚠ {actionError}
            </div>
          )}

          {loading ? <div className="loading">...</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>作品名</th><th>種別</th><th>単価</th><th>ページ/話</th><th>状態</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleWorks.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
                        作品がありません
                      </td>
                    </tr>
                  )}
                  {visibleWorks.map((w) => (
                    <tr key={w.id} style={{ opacity: w.archived_at ? 0.6 : 1 }}>
                      <td style={{ fontWeight: 500 }}>{w.title}</td>
                      <td>{FREQUENCY_LABELS[w.frequency] ?? w.frequency}</td>
                      <td className="mono">¥{w.rate_per_page.toLocaleString()}</td>
                      <td className="mono">{w.pages_per_ep}P</td>
                      <td>
                        {w.archived_at
                          ? <span className="badge badge-gray">アーカイブ済み</span>
                          : <span className="badge badge-green">通常</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditTarget(w)}>編集</button>
                          {w.archived_at ? (
                            <button className="btn btn-ghost btn-sm" onClick={() => doRestore(w)}>復元</button>
                          ) : (
                            <button className="btn btn-ghost btn-sm" onClick={() => doArchive(w)}>アーカイブ</button>
                          )}
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                            onClick={() => setDeleteConfirm({ id: w.id, title: w.title })}>
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editTarget && (
        <WorkFormModal
          work={editTarget === 'new' ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => { setEditTarget(null); await load(); onChanged() }}
        />
      )}

      {deleteConfirm && (
        <ConfirmDialog
          title="作品を削除しますか？"
          message={`「${deleteConfirm.title}」を削除します。\nこの操作は元に戻せません。`}
          confirmLabel="削除する"
          confirmDanger
          onConfirm={async () => { await doDelete(deleteConfirm.id); setDeleteConfirm(null) }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  )
}

// =====================================================
// 締切編集モーダル（管理者のみ・工程の締切日だけを変更する）
// =====================================================
const DEADLINE_FIELDS: { key: 'deadline_name_check' | 'deadline_submit' | 'deadline_entry'; label: string }[] = [
  { key: 'deadline_name_check', label: 'ネーム確認締切' },
  { key: 'deadline_submit',     label: '提出締切' },
  { key: 'deadline_entry',      label: '入稿' },
]

function DeadlineEditModal({ episode, onClose, onSaved }: {
  episode: Episode
  onClose: () => void
  onSaved: () => void
}) {
  const initial: Record<string, string> = {
    deadline_name_check: episode.deadline_name_check ?? '',
    deadline_submit:     episode.deadline_submit     ?? '',
    deadline_entry:      episode.deadline_entry      ?? '',
  }
  const [values, setValues] = useState<Record<string, string>>(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmOverwrite, setConfirmOverwrite] = useState(false)

  // 初期値と異なるフィールドのみが「変更対象」。空欄のまま＝未変更、
  // 値ありから空欄に変えた場合は「締切なしにする」明示的な変更として扱う。
  const changedEntries = DEADLINE_FIELDS
    .filter((f) => values[f.key] !== initial[f.key])
    .map((f) => [f.key, values[f.key]] as const)

  const doSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const patch: Record<string, string | null> = {}
      for (const [key, v] of changedEntries) patch[key] = v === '' ? null : v
      await api.episodes.update(episode.id, patch)
      onSaved()
      onClose()
    } catch (ex: any) {
      setError(ex.message ?? 'エラーが発生しました')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveClick = () => {
    if (changedEntries.length === 0) { onClose(); return }
    // 既存の締切（空欄以外）を上書き・またはクリアする場合は確認を挟む
    const overwritesExisting = changedEntries.some(([key]) => initial[key] !== '')
    if (overwritesExisting) { setConfirmOverwrite(true); return }
    doSave()
  }

  return (
    <>
      <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ maxWidth: 480 }}>
          <div className="modal-header">
            <div className="modal-title">締切を変更</div>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>{episode.work_title}</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{epLabel(episode)}</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {DEADLINE_FIELDS.map((f) => (
                <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>{f.label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 96 }}>
                      現在: {initial[f.key] || '未設定'}
                    </span>
                    <input type="date" value={values[f.key]}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      style={{ flex: 1, minWidth: 140 }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 14, lineHeight: 1.6 }}>
              空欄のまま保存すると、その締切は変更されません。既存の締切をクリアするには入力欄を空にして保存してください。
              ページ数・工程進捗は変更されません。
            </div>
          </div>
          {error && (
            <div style={{
              padding: '8px 16px', background: 'var(--red-light, #fef2f2)',
              borderTop: '1px solid var(--red, #ef4444)', color: 'var(--red, #ef4444)', fontSize: 13,
            }}>
              {error}
            </div>
          )}
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
            <button className="btn btn-primary" onClick={handleSaveClick} disabled={saving || changedEntries.length === 0}>
              {saving ? '保存中...' : '保存する'}
            </button>
          </div>
        </div>
      </div>
      {confirmOverwrite && (
        <ConfirmDialog
          title="締切を上書きしますか？"
          message="既存の締切日を上書き、またはクリアします。この操作は元に戻せません。"
          confirmLabel="上書きする"
          onConfirm={() => { setConfirmOverwrite(false); doSave() }}
          onClose={() => setConfirmOverwrite(false)}
        />
      )}
    </>
  )
}

// =====================================================
// メインページ
// =====================================================
export default function Works() {
  const { isAdmin } = useAuth()
  const [works, setWorks]           = useState<Work[]>([])
  const [episodes, setEpisodes]     = useState<Episode[]>([])
  const [selectedWork, setSelectedWork] = useState<string>('all')
  const [loading, setLoading]       = useState(true)
  const [showAddEp, setShowAddEp]   = useState(false)
  const [stageEp, setStageEp]       = useState<Episode | null>(null)
  const [deadlineEp, setDeadlineEp] = useState<Episode | null>(null)
  const [undoState, setUndoState]   = useState<{ id: string; label: string } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; label: string } | null>(null)
  // ★ アーカイブ表示切り替え
  const [showArchived, setShowArchived] = useState(false)
  // ★ TodayPlanPanel 再読み込みキー
  const [planKey, setPlanKey] = useState(0)
  // ★ 作品管理モーダル（作品追加はこの中の導線として提供する）
  const [showWorksManager, setShowWorksManager] = useState(false)

  const load = async () => {
    const [ws, eps] = await Promise.all([
      api.works.list(),
      api.episodes.list(),
    ])
    setWorks(ws)
    setEpisodes(eps)
    setLoading(false)
    // 今日の制作指示パネルも再計算させる
    setPlanKey((k) => k + 1)
  }

  useEffect(() => { load() }, [])

  const doDeleteEpisode = async (id: string) => {
    await api.episodes.delete(id)
    await load()
  }

  // ★ 6か月カットオフ（フロントで計算。DB変更なし）
  const today = todayJst()
  const [ty, tm, td] = today.split('-').map(Number)
  const sixMonthsAgoDate = new Date(ty, tm - 7, td)
  const cutoff = `${sixMonthsAgoDate.getFullYear()}-${String(sixMonthsAgoDate.getMonth() + 1).padStart(2, '0')}-${String(sixMonthsAgoDate.getDate()).padStart(2, '0')}`

  // アーカイブ判定: completed/submitted かつ deadline_submit が6か月以上前
  const isArchived = (ep: Episode) =>
    DONE_STATUSES.has(ep.status) &&
    ep.deadline_submit !== null &&
    ep.deadline_submit < cutoff

  const filteredByWork = selectedWork === 'all'
    ? episodes
    : episodes.filter((e) => e.work_id === selectedWork)

  const filteredEps    = showArchived ? filteredByWork : filteredByWork.filter((ep) => !isArchived(ep))
  const archivedCount  = filteredByWork.filter(isArchived).length

  // 締切近い順ソート（完了済み工程の締切は除外して判定）
  // アクティブな締切がない話数（完了済みなど）は末尾
  const sortedEps = [...filteredEps].sort((a, b) => {
    const da = nearestActiveDeadline(a)
    const db = nearestActiveDeadline(b)
    if (da === null && db === null) return 0
    if (da === null) return 1
    if (db === null) return -1
    return da.getTime() - db.getTime()
  })

  if (loading) return <div className="loading">LOADING...</div>

  return (
    <div>
      {/* 今日の制作指示パネル（工程進捗保存後に planKey が変わり再計算） */}
      <TodayPlanPanel reloadKey={planKey} />

      {/* 保存済み制作予定パネル（Phase 4-B: production_plan_items ベース） */}
      <TodayProductionPlanPanel compact reloadKey={planKey} />

      {deleteConfirm && (
        <ConfirmDialog
          title="話数を削除しますか？"
          message={`「${deleteConfirm.label}」を削除します。\n制作工程データもすべて削除されます。\nこの操作は取り消せません。`}
          confirmLabel="削除する"
          onConfirm={async () => { await doDeleteEpisode(deleteConfirm.id); setDeleteConfirm(null) }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {/* 作品タブ + 作品管理導線 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${selectedWork === 'all' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setSelectedWork('all')}>
            すべて ({episodes.length})
          </button>
          {works.map((w) => (
            <button key={w.id}
              className={`btn ${selectedWork === w.id ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setSelectedWork(w.id)}>
              {w.title}
              <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>
                {w.active_episodes ?? 0}話進行中
              </span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {/* ★ 作品追加は「作品を管理」の中に移動（＋話数追加との誤操作防止のため通常表示からは削除） */}
          <button className="btn btn-outline btn-sm" onClick={() => setShowWorksManager(true)}>
            作品を管理
          </button>
        </div>
      </div>

      {/* 作品サマリー */}
      {selectedWork !== 'all' && (() => {
        const w = works.find((x) => x.id === selectedWork)
        if (!w) return null
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body">
              <div className="grid-4">
                <div className="metric"><div className="metric-label">1ページ単価</div><div className="metric-value">¥{w.rate_per_page.toLocaleString()}</div></div>
                <div className="metric"><div className="metric-label">1話ページ数</div><div className="metric-value">{w.pages_per_ep}P</div></div>
                <div className="metric"><div className="metric-label">1話原稿料</div><div className="metric-value">¥{(w.rate_per_page * w.pages_per_ep).toLocaleString()}</div></div>
                <div className="metric"><div className="metric-label">原作あり</div><div className="metric-value">{w.has_original_story ? 'はい' : 'なし'}</div></div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* 話数テーブル */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">話数一覧</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* ★ アーカイブ表示切り替え */}
            {archivedCount > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--ink-3)' }}
                onClick={() => setShowArchived(!showArchived)}>
                {showArchived ? 'アーカイブを非表示' : `完了済み${archivedCount}件を表示`}
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddEp(true)}>
              + 話数追加
            </button>
          </div>
        </div>

        {/* アーカイブ非表示の注記 */}
        {!showArchived && archivedCount > 0 && (
          <div style={{
            padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)',
            borderBottom: '1px solid var(--paper-2)', background: 'var(--paper-2)',
          }}>
            完了済みの話数（6か月以上前）{archivedCount}件を非表示中
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>作品</th><th>種別</th><th>話数/ラベル</th><th>ページ</th><th>ステータス</th><th>工程</th>
                <th>ネーム締切</th><th>提出締切</th><th>入稿</th><th>原稿料</th><th></th>
              </tr>
            </thead>
            <tbody>
              {sortedEps.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-muted" style={{ textAlign: 'center', padding: 32 }}>
                    話数がありません
                  </td>
                </tr>
              )}
              {sortedEps.map((ep) => {
                // 話数全体の完了判定（行の opacity）
                const episodeDone  = isEpisodeEffectivelyDone(ep)
                // 締切ごとの完了判定
                const nameDone     = isNameCheckDone(ep)   // deadline_name_check
                const submitDone   = isSubmitDone(ep)      // deadline_submit / deadline_entry
                const wt = (ep.work_type ?? 'story') as string
                return (
                <tr key={ep.id} style={{ opacity: episodeDone ? 0.65 : 1 }}>
                  <td
                    style={{ fontSize: 12, fontWeight: 500, maxWidth: 96, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={ep.work_title ?? undefined}>
                    {ep.work_title}
                  </td>
                  <td>
                    <span className={`badge ${WORK_TYPE_BADGE[wt] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>
                      {WORK_TYPE_LABELS[wt] ?? wt}
                    </span>
                  </td>
                  <td className="mono" style={{ fontWeight: 700, maxWidth: 160,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={epLabel(ep)}>
                    {epLabel(ep)}
                  </td>
                  <td className="mono" style={{ cursor: 'pointer' }}
                    title="クリックでページ数を編集"
                    onClick={() => {
                      const displayLabel = `${ep.work_title} ${epLabel(ep)}`
                      const v = prompt(`${displayLabel} のページ数`, String(ep.page_count ?? 24))
                      if (v === null) return
                      const n = parseInt(v, 10)
                      if (isNaN(n) || n <= 0) return
                      api.episodes.update(ep.id, { page_count: n }).then(load)
                    }}>
                    {ep.page_count}P ✎
                  </td>
                  <td><StatusBadge status={ep.status} /></td>
                  <td><StagePips summary={ep.stage_summary} progressSummary={ep.stage_progress_summary} /></td>
                  {/* ネーム締切: name 工程が done なら — 表示 */}
                  <td><DeadlineChip days={daysUntil(ep.deadline_name_check)} status={nameDone    ? 'completed' : ep.status} /></td>
                  {/* 提出締切: submitted 工程が done なら — 表示 */}
                  <td><DeadlineChip days={daysUntil(ep.deadline_submit)}     status={submitDone  ? 'completed' : ep.status} /></td>
                  {/* 入稿: submitted 工程が done なら — 表示 */}
                  <td><DeadlineChip days={daysUntil(ep.deadline_entry)}      status={submitDone  ? 'completed' : ep.status} /></td>
                  <td className="mono">
                    {ep.estimated_fee ? `¥${ep.estimated_fee.toLocaleString()}` : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setStageEp(ep)}>工程更新</button>
                      {isAdmin && (
                        <button className="btn btn-ghost btn-sm" title="工程締切を編集"
                          onClick={() => setDeadlineEp(ep)}>
                          ✎ 締切
                        </button>
                      )}
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                        onClick={() => setDeleteConfirm({ id: ep.id, label: `${ep.work_title} ${epLabel(ep)}` })}>
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAddEp && (
        <AddEpisodeModal works={works}
          defaultWorkId={selectedWork !== 'all' ? selectedWork : undefined}
          onClose={() => setShowAddEp(false)}
          onSaved={(id, workTitle, label) => {
            setShowAddEp(false); load()
            if (id) setUndoState({ id, label: `${workTitle} ${label}` })
          }}
        />
      )}
      {undoState && (
        <UndoToast msg={`「${undoState.label}」を追加しました`}
          onUndo={async () => { await api.episodes.delete(undoState.id); await load() }}
          onClose={() => setUndoState(null)}
        />
      )}
      {stageEp && (
        <StageModal episode={stageEp} onClose={() => setStageEp(null)} onSaved={load} />
      )}
      {deadlineEp && isAdmin && (
        <DeadlineEditModal episode={deadlineEp} onClose={() => setDeadlineEp(null)} onSaved={load} />
      )}

      {showWorksManager && (
        <WorksManagerModal
          onClose={() => setShowWorksManager(false)}
          onChanged={load}
        />
      )}
    </div>
  )
}